import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Recycle, SignOut, Buildings, Users, Receipt, TrendUp, Plus, ArrowRight, Briefcase } from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import usePageTitle from "@/lib/usePageTitle";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

export default function SuperAdmin() {
  usePageTitle("Super-Admin");
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [franchise, setFranchise] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ slug: "", name: "", email: "", primary_color: "#2563EB", secondary_color: "#0EA5E9", tagline: "" });
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "platform_admin") return;
    refresh();
  }, [user]);

  const refresh = async () => {
    try {
      const [m, t, f] = await Promise.all([
        api.get("/metrics/platform"),
        api.get("/tenants"),
        api.get("/franchise/applications").catch(() => ({ data: [] })),
      ]);
      setMetrics(m.data);
      setTenants(t.data);
      setFranchise(f.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Lade…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "platform_admin") return <Navigate to="/me" replace />;

  const createTenant = async () => {
    try {
      await api.post("/tenants", form);
      setCreating(false);
      setForm({ slug: "", name: "", email: "", primary_color: "#2563EB", secondary_color: "#0EA5E9", tagline: "" });
      refresh();
    } catch (e) {
      alert("Fehler: " + (e.response?.data?.detail || e.message));
    }
  };

  const seed = async () => {
    setSeeding(true);
    try {
      await api.post("/seed/demo");
      await refresh();
    } finally {
      setSeeding(false);
    }
  };

  const eur = (cents) => `€${(cents / 100).toFixed(2)}`;

  return (
    <div className="min-h-screen bg-[var(--tonneva-bg)]">
      <header className="bg-[#0e1a23] text-white">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/brand/tonneva-logo.png" alt="Tonneva" className="w-8 h-8" />
            <span className="font-heading font-bold tracking-tight">Tonneva</span>
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: "var(--tonneva-secondary)" }}>Super-Admin</span>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <span className="text-white/60">{user.email}</span>
            <ChangePasswordDialog className="text-white/70 hover:text-white" />
            <button onClick={logout} className="flex items-center gap-1.5 text-white/70 hover:text-white" data-testid="logout-btn">
              <SignOut size={14} /> Abmelden
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[var(--tonneva-primary)]">Übersicht</span>
            <h1 className="font-heading text-4xl font-black tracking-tight mt-1">Plattform-Kontrollraum</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={seed} disabled={seeding} className="rounded-none border-black/20" data-testid="seed-demo-btn">
              {seeding ? "Lade Demo…" : "Demo-Daten laden"}
            </Button>
            <Dialog open={creating} onOpenChange={setCreating}>
              <DialogTrigger asChild>
                <Button className="bg-tonneva-gradient hover:opacity-90 rounded-full text-white" data-testid="open-create-tenant">
                  <Plus className="mr-1.5" size={14} weight="bold" /> Mandant anlegen
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none border-black/20" data-testid="create-tenant-dialog">
                <DialogHeader><DialogTitle className="font-heading text-2xl">Neuer Mandant</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <Field label="Firmenname" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="tenant-name" />
                  <Field label="Slug (URL)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} testId="tenant-slug" />
                  <Field label="E-Mail" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="tenant-email" />
                  <Field label="Tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} testId="tenant-tagline" />
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Primärfarbe</Label>
                      <Input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="h-11 rounded-none p-1" data-testid="tenant-primary-color" />
                    </div>
                    <div>
                      <Label className="text-xs">Sekundärfarbe</Label>
                      <Input type="color" value={form.secondary_color} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} className="h-11 rounded-none p-1" data-testid="tenant-secondary-color" />
                    </div>
                  </div>
                  <Button onClick={createTenant} className="w-full bg-black text-white hover:bg-zinc-800 rounded-none mt-2" data-testid="submit-create-tenant">Mandant erstellen</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          <MetricCard icon={<Buildings size={16} weight="bold" />} label="Mandanten" value={metrics?.tenants ?? "—"} />
          <MetricCard icon={<Users size={16} weight="bold" />} label="Endkunden" value={metrics?.customers ?? "—"} />
          <MetricCard icon={<Recycle size={16} weight="bold" />} label="Aktive Abos" value={metrics?.active_subscriptions ?? "—"} />
          <MetricCard icon={<Receipt size={16} weight="bold" />} label="Bruttovolumen" value={metrics ? eur(metrics.gross_volume_cents) : "—"} />
          <MetricCard icon={<TrendUp size={16} weight="bold" />} label="Plattform-Umsatz" value={metrics ? eur(metrics.platform_revenue_cents) : "—"} accent />
        </div>

        {/* Tenants list */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl font-bold tracking-tight">Mandanten</h2>
            <span className="text-xs text-[var(--tonneva-muted)]">{tenants.length} aktiv</span>
          </div>
          {tenants.length === 0 ? (
            <div className="border border-dashed border-black/20 p-12 text-center bg-white">
              <p className="text-[var(--tonneva-muted)]">Noch keine Mandanten. Klicken Sie &quot;Demo-Daten laden&quot; oder erstellen Sie einen.</p>
            </div>
          ) : (
            <div className="bg-white border border-black/10">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-left text-[10px] uppercase tracking-widest text-[var(--tonneva-muted)]">
                  <tr>
                    <th className="px-4 py-3">Mandant</th>
                    <th className="px-4 py-3">Slug</th>
                    <th className="px-4 py-3">Stadt</th>
                    <th className="px-4 py-3">CI</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.map((t) => (
                    <tr key={t.tenant_id} className="border-t border-black/10 hover:bg-zinc-50 transition" data-testid={`tenant-row-${t.slug}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{t.name}</div>
                        <div className="text-xs text-[var(--tonneva-muted)]">{t.email}</div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{t.slug}</td>
                      <td className="px-4 py-3">{t.city || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <span className="w-5 h-5 border border-black/10" style={{ background: t.branding?.primary_color }} />
                          <span className="w-5 h-5 border border-black/10" style={{ background: t.branding?.secondary_color }} />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] uppercase tracking-widest font-bold ${t.active ? "text-green-600" : "text-zinc-400"}`}>
                          {t.active ? "Aktiv" : "Inaktiv"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => window.open(`/portal/${t.slug}`, "_blank")} className="text-xs text-[var(--tonneva-primary)] hover:underline" data-testid={`view-portal-${t.slug}`}>
                          Portal ansehen <ArrowRight className="inline" size={10} weight="bold" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Franchise Applications */}
        <section className="mt-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
              <Briefcase size={20} weight="bold" style={{ color: "var(--tonneva-secondary)" }} /> Franchise-Anfragen
            </h2>
            <span className="text-xs text-[var(--tonneva-muted)]">{franchise.length} insgesamt · {franchise.filter((a) => a.status === "new").length} neu</span>
          </div>
          {franchise.length === 0 ? (
            <div className="border border-dashed border-black/20 p-12 text-center bg-white">
              <p className="text-[var(--tonneva-muted)]">Noch keine Bewerbungen.</p>
            </div>
          ) : (
            <div className="bg-white border border-black/10">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-left text-[10px] uppercase tracking-widest text-[var(--tonneva-muted)]">
                  <tr>
                    <th className="px-4 py-3">Bewerber</th>
                    <th className="px-4 py-3">Region</th>
                    <th className="px-4 py-3">Budget</th>
                    <th className="px-4 py-3">Bedarf</th>
                    <th className="px-4 py-3">Eingegangen</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {franchise.map((a) => (
                    <FranchiseRow key={a.application_id} a={a} onChange={refresh} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Bank-Profile für SEPA */}
        <BankProfilesSection />
        <SmtpSettingsSection />
        <AuditLogSection />
      </main>
    </div>
  );
}

function FranchiseRow({ a, onChange }) {
  const setStatus = async (status) => {
    try { await api.patch(`/franchise/applications/${a.application_id}`, { status }); onChange(); } catch (e) { alert(e.message); }
  };
  const needs = [a.needs_vehicle && "Fahrzeug", a.needs_equipment && "Equipment", a.needs_marketing && "Marketing"].filter(Boolean).join(" · ");
  const statusColor = { new: "#3FA64E", in_review: "#F59E0B", approved: "#2A86A8", rejected: "#9CA3AF", onboarded: "#1d6781" }[a.status] || "#9CA3AF";
  return (
    <tr className="border-t border-black/10 hover:bg-zinc-50 transition" data-testid={`franchise-row-${a.application_id}`}>
      <td className="px-4 py-3">
        <div className="font-medium">{a.contact_name}{a.company_name ? ` (${a.company_name})` : ""}</div>
        <div className="text-xs text-[var(--tonneva-muted)]">{a.contact_email}{a.contact_phone ? ` · ${a.contact_phone}` : ""}</div>
        {a.has_experience && <span className="text-[10px] uppercase tracking-widest font-bold mt-1 inline-block" style={{ color: "var(--tonneva-secondary)" }}>Mit Erfahrung</span>}
      </td>
      <td className="px-4 py-3 font-medium">{a.proposed_region}</td>
      <td className="px-4 py-3 font-mono text-xs">{a.investment_budget_eur ? `€${a.investment_budget_eur.toLocaleString("de-DE")}` : "—"}</td>
      <td className="px-4 py-3 text-xs text-[var(--tonneva-muted)]">{needs || "—"}</td>
      <td className="px-4 py-3 text-xs text-[var(--tonneva-muted)]">{new Date(a.created_at).toLocaleDateString("de-DE")}</td>
      <td className="px-4 py-3">
        <Select value={a.status} onValueChange={setStatus}>
          <SelectTrigger className="h-8 rounded-full text-xs w-32 border-2" style={{ borderColor: statusColor, color: statusColor, fontWeight: 600 }} data-testid={`franchise-status-${a.application_id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="new">Neu</SelectItem>
            <SelectItem value="in_review">In Prüfung</SelectItem>
            <SelectItem value="approved">Genehmigt</SelectItem>
            <SelectItem value="rejected">Abgelehnt</SelectItem>
            <SelectItem value="onboarded">Onboarded</SelectItem>
          </SelectContent>
        </Select>
      </td>
    </tr>
  );
}

const MetricCard = ({ icon, label, value, accent }) => (
  <div className={`p-5 border ${accent ? "bg-black text-white border-black" : "bg-white border-black/10"}`}>
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-70 mb-2">{icon} {label}</div>
    <div className="font-mono text-2xl font-medium tracking-tight">{value}</div>
  </div>
);

const Field = ({ label, value, onChange, testId }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input value={value} onChange={(e) => onChange(e.target.value)} className="h-11 rounded-none" data-testid={testId} />
  </div>
);

function SmtpSettingsSection() {
  const [f, setF] = useState({ host: "", port: 465, security: "ssl", username: "", from_name: "Tonneva", from_email: "", enabled: true });
  const [password, setPassword] = useState("");
  const [passwordSet, setPasswordSet] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/smtp-settings");
      const d = r.data || {};
      setF({ host: d.host || "", port: d.port || 465, security: d.security || "ssl", username: d.username || "", from_name: d.from_name || "Tonneva", from_email: d.from_email || "", enabled: !!d.enabled });
      setPasswordSet(!!d.password_set);
      setTestTo(d.from_email || d.username || "");
    } catch (e) { setMsg({ type: "err", text: e.message }); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const payload = { ...f };
      if (password) payload.password = password;
      const r = await api.put("/admin/smtp-settings", payload);
      setPasswordSet(!!r.data.password_set);
      setPassword("");
      setMsg({ type: "ok", text: "SMTP-Einstellungen gespeichert." });
    } catch (e) { setMsg({ type: "err", text: e.message }); }
    setSaving(false);
  };

  const sendTest = async () => {
    if (!testTo || !testTo.includes("@")) { setMsg({ type: "err", text: "Bitte gültige Test-Empfängeradresse angeben." }); return; }
    setTesting(true); setMsg(null);
    try {
      const r = await api.post("/admin/smtp-settings/test", { to: testTo });
      if (r.data.ok) setMsg({ type: "ok", text: `Test-E-Mail an ${testTo} gesendet.` });
      else setMsg({ type: "err", text: `Versand fehlgeschlagen: ${r.data.error}` });
    } catch (e) { setMsg({ type: "err", text: e.message }); }
    setTesting(false);
  };

  return (
    <section className="mt-12" data-testid="smtp-settings-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
          <Receipt size={20} weight="bold" style={{ color: "var(--tonneva-secondary)" }} /> E-Mail-Versand (SMTP)
        </h2>
        <label className="flex items-center gap-2 text-xs text-[var(--tonneva-muted)]">
          <input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} data-testid="smtp-enabled-toggle" />
          Versand aktiv
        </label>
      </div>
      <div className="bg-white border border-black/10 p-6 max-w-3xl">
        <p className="text-sm text-[var(--tonneva-muted)] mb-5">Zentraler SMTP-Server für alle ausgehenden E-Mails der Plattform (z. B. Interessenten-Benachrichtigungen, Bestätigungen).</p>
        {loading ? <p className="text-sm">Lädt…</p> : (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="SMTP-Host" value={f.host} onChange={(v) => setF({ ...f, host: v })} testId="smtp-host" />
              <Field label="Port" value={f.port} onChange={(v) => setF({ ...f, port: v })} testId="smtp-port" />
              <div>
                <Label className="text-xs">Verschlüsselung</Label>
                <Select value={f.security} onValueChange={(v) => setF({ ...f, security: v })}>
                  <SelectTrigger className="h-11 rounded-none" data-testid="smtp-security"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ssl">SSL/TLS (Port 465)</SelectItem>
                    <SelectItem value="starttls">STARTTLS (Port 587)</SelectItem>
                    <SelectItem value="none">Keine (Port 25)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field label="Benutzername" value={f.username} onChange={(v) => setF({ ...f, username: v })} testId="smtp-username" />
              <div>
                <Label className="text-xs">Passwort {passwordSet && <span className="text-[var(--tonneva-muted)]">(gesetzt – leer lassen zum Beibehalten)</span>}</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={passwordSet ? "••••••••" : ""} className="h-11 rounded-none" data-testid="smtp-password" />
              </div>
              <Field label="Absender-Name" value={f.from_name} onChange={(v) => setF({ ...f, from_name: v })} testId="smtp-from-name" />
              <Field label="Absender-E-Mail" value={f.from_email} onChange={(v) => setF({ ...f, from_email: v })} testId="smtp-from-email" />
            </div>
            {msg && <div className={`mt-4 text-sm ${msg.type === "ok" ? "text-green-700" : "text-red-600"}`} data-testid="smtp-msg">{msg.text}</div>}
            <div className="flex flex-wrap items-end gap-3 mt-5 pt-5 border-t border-black/10">
              <Button onClick={save} disabled={saving} className="rounded-none" data-testid="smtp-save-btn">{saving ? "Speichert…" : "Speichern"}</Button>
              <div className="flex items-end gap-2">
                <div>
                  <Label className="text-xs">Test an</Label>
                  <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} className="h-11 rounded-none w-64" data-testid="smtp-test-to" />
                </div>
                <Button variant="outline" onClick={sendTest} disabled={testing} className="rounded-none" data-testid="smtp-test-btn">{testing ? "Sendet…" : "Test-E-Mail senden"}</Button>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function AuditLogSection() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyVies, setBusyVies] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/audit-log?limit=200"); setEvents(r.data.events || []); }
    catch (e) { console.warn("[SuperAdmin] audit-log load failed", e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const runVies = async () => {
    setBusyVies(true); setMsg(null);
    try {
      const r = await api.post("/admin/vies-revalidate-all");
      setMsg({ ok: true, msg: `VIES-Prüfung: ${r.data.checked} geprüft · ${r.data.still_valid} gültig · ${r.data.now_invalid} ungültig · ${r.data.notified} benachrichtigt` });
      await load();
    } catch (e) { setMsg({ ok: false, msg: e.response?.data?.detail || "Fehler" }); }
    finally { setBusyVies(false); setTimeout(() => setMsg(null), 6000); }
  };
  const typeLabel = (t) => {
    if (t === "gdpr_retention_cleanup") return { label: "🗑️ Retention-Cleanup", color: "bg-zinc-100 text-zinc-800" };
    if (t === "vies_revalidation") return { label: "🇪🇺 VIES-Revalidation", color: "bg-blue-100 text-blue-800" };
    if (t === "consent_revoke_consent_full" || t === "consent_revoke_full") return { label: "🔒 Datenschutz-Widerruf", color: "bg-red-100 text-red-800" };
    if (t === "consent_revoke_newsletter_only") return { label: "📧 Newsletter-Abmeldung", color: "bg-amber-100 text-amber-800" };
    return { label: t, color: "bg-zinc-100 text-zinc-700" };
  };
  return (
    <section className="mt-12" data-testid="audit-log-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-heading text-2xl font-bold tracking-tight">Compliance-Audit-Log</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={runVies}
            disabled={busyVies}
            className="text-xs font-medium px-3 py-1.5 rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            data-testid="run-vies-btn"
            title="Alle gespeicherten USt-IdNr. sofort per VIES prüfen"
          >
            {busyVies ? "Prüfe…" : "🇪🇺 VIES-Revalidation jetzt starten"}
          </button>
          <button
            onClick={load}
            className="text-xs font-medium px-3 py-1.5 rounded-full border border-black/10 hover:bg-zinc-50"
            data-testid="audit-refresh-btn"
          >
            Aktualisieren
          </button>
        </div>
      </div>
      <p className="text-xs text-[var(--tonneva-muted)] mb-4">
        Timeline aller Compliance-Events (Datenschutz-Widerrufe, Newsletter-Abmeldungen, Aufbewahrungs-Cleanups, VIES-Revalidationen).
      </p>
      {msg && <div className={`mb-4 text-sm ${msg.ok ? "text-emerald-700" : "text-red-600"}`}>{msg.ok ? "✓" : "✗"} {msg.msg}</div>}
      {loading ? (
        <div className="bg-white border border-black/10 p-8 text-center text-zinc-400">Lade Audit-Log…</div>
      ) : events.length === 0 ? (
        <div className="bg-white border border-black/10 p-8 text-center text-zinc-400">Noch keine Compliance-Events erfasst.</div>
      ) : (
        <div className="bg-white border border-black/10 divide-y divide-black/5" data-testid="audit-events">
          {events.map((e, i) => {
            const meta = typeLabel(e.event_type);
            return (
              <div key={e.audit_id || i} className="p-4 flex items-start gap-4" data-testid={`audit-event-${i}`}>
                <div className="w-32 flex-shrink-0 font-mono text-[10px] text-zinc-500 pt-1">
                  {e.timestamp ? new Date(e.timestamp).toLocaleString("de-DE") : "—"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded font-bold ${meta.color}`}>{meta.label}</span>
                    {e.tenant_id && <span className="text-[10px] font-mono text-zinc-500">{e.tenant_id.slice(0, 20)}</span>}
                    {e.email && <span className="text-xs text-zinc-700 font-mono">{e.email}</span>}
                  </div>
                  {e.detail && (
                    <div className="text-xs text-zinc-500 mt-1">
                      {e.event_type === "vies_revalidation" && e.detail.result && `${e.detail.result.checked || 0} geprüft · ${e.detail.result.still_valid || 0} gültig · ${e.detail.result.now_invalid || 0} ungültig`}
                      {e.event_type === "gdpr_retention_cleanup" && `${e.detail.deleted_count} Records gelöscht (Cutoff: ${e.detail.cutoff?.slice(0, 10) || "?"})`}
                      {e.detail.reason && ` · Grund: "${e.detail.reason}"`}
                      {e.detail.ip && ` · IP ${e.detail.ip}`}
                      {e.detail.method && ` · via ${e.detail.method}`}
                      {e.detail.lead_id && ` · Lead ${e.detail.lead_id}`}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function BankProfilesSection() {
  const [profiles, setProfiles] = useState({});
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState(null);
  const load = async () => {
    try { const r = await api.get("/superadmin/bank-profiles"); setProfiles(r.data.profiles || {}); } catch (e) { console.warn("[SuperAdmin] bank-profiles load failed", e); }
  };
  useEffect(() => { load(); }, []);
  const save = async (key, patch) => {
    try {
      await api.patch(`/superadmin/bank-profiles/${key}`, patch);
      setMsg({ ok: true, msg: `${key} gespeichert` });
      setEditing(null);
      await load();
      setTimeout(() => setMsg(null), 2500);
    } catch (e) { setMsg({ ok: false, msg: e.response?.data?.detail || "Fehler" }); }
  };
  return (
    <section className="mt-12" data-testid="bank-profiles-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-2xl font-bold tracking-tight">SEPA Bank-Profile</h2>
        {msg && <span className={`text-xs font-medium ${msg.ok ? "text-emerald-700" : "text-red-600"}`}>{msg.ok ? "✓" : "✗"} {msg.msg}</span>}
      </div>
      <p className="text-xs text-[var(--tonneva-muted)] mb-4">Steuert wie das SEPA-XML gebaut wird (BIC-Pflicht, Umlaut-Konvertierung, Zeichenlängen). Änderungen wirken auf alle Mandanten mit diesem Profil.</p>
      <div className="bg-white border border-black/10">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-left text-[10px] uppercase tracking-widest text-[var(--tonneva-muted)]">
            <tr><th className="px-4 py-3">Key</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">BIC-Pflicht</th><th className="px-4 py-3">Umlaut-Konvertierung</th><th className="px-4 py-3">Max. Ref-Länge</th><th className="px-4 py-3">Aktion</th></tr>
          </thead>
          <tbody>
            {Object.entries(profiles).map(([k, p]) => {
              const isEditing = editing === k;
              return (
                <tr key={k} className="border-t border-black/5" data-testid={`bank-profile-${k}`}>
                  <td className="px-4 py-3 font-mono text-xs">{k}</td>
                  <td className="px-4 py-3">
                    {isEditing
                      ? <Input className="h-8 rounded-none" defaultValue={p.name} onBlur={(e) => (p.name = e.target.value)} />
                      : <span className="font-medium">{p.name}</span>}
                  </td>
                  <td className="px-4 py-3">
                    {isEditing
                      ? <input type="checkbox" defaultChecked={p.bic_required} onChange={(e) => (p.bic_required = e.target.checked)} className="w-5 h-5" />
                      : (p.bic_required ? "Ja" : "Nein")}
                  </td>
                  <td className="px-4 py-3">
                    {isEditing
                      ? <input type="checkbox" defaultChecked={p.strip_umlauts} onChange={(e) => (p.strip_umlauts = e.target.checked)} className="w-5 h-5" />
                      : (p.strip_umlauts ? "Ja (ä→ae)" : "Nein")}
                  </td>
                  <td className="px-4 py-3 font-mono">
                    {isEditing
                      ? <Input type="number" className="h-8 w-20 rounded-none" defaultValue={p.max_ref_length} onBlur={(e) => (p.max_ref_length = parseInt(e.target.value))} />
                      : `${p.max_ref_length}`}
                  </td>
                  <td className="px-4 py-3">
                    {isEditing
                      ? <div className="flex gap-2"><button onClick={() => save(k, p)} className="text-xs px-3 py-1 rounded bg-black text-white" data-testid={`bp-save-${k}`}>Speichern</button><button onClick={() => setEditing(null)} className="text-xs px-3 py-1 rounded border border-black/10">Abbrechen</button></div>
                      : <button onClick={() => setEditing(k)} className="text-xs px-3 py-1 rounded border border-black/10 hover:bg-zinc-50" data-testid={`bp-edit-${k}`}>Bearbeiten</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
